import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import useListAction from 'core/hooks/useListAction'
import {
  listResmgrHosts,
  getResmgrRoleConfig,
  updateRemoteSupportRole,
  removeRemoteSupportRole,
  getBlueprints,
  assignHypervisorRole,
  assignImageLibraryRole,
  assignPersistentStorageRole,
  deauthorizeHypervisorRole,
  deauthorizeImageLibraryRole,
  deauthorizePersistentStorageRole,
  assignHostConfig,
  unassignHostConfig,
  getVmsForHost,
  removeDns,
  assignDns,
} from '../actions'
import { resmgrHostsSelector } from '../selectors'
import {
  hasHypervisorRole,
  hasImageLibraryRole,
  hasCinderRole,
  hasRemoteSupportRole,
  getHostRoleConfig,
  hasDnsRole,
} from '../helpers'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import MultiDropdownField from 'core/components/validatedForm/MultiDropdownField'
import { useSelector } from 'react-redux'
import { useAppSelector } from 'app/store'
import PicklistField from 'core/components/validatedForm/DropdownField'
import HostNetworkConfigPicklist from './HostNetworkConfigPicklist'
import { keys } from 'ramda'
import { listImages } from 'openstack/components/images/actions'
import { imagesSelector } from 'openstack/components/images/selectors'
import CodeBlock from 'core/components/CodeBlock'
import { DNS_DOC_LINK } from './helpers'
import ExternalLink from 'core/components/ExternalLink'
import ApiClient from 'api-client/ApiClient'
import Info from 'core/components/validatedForm/Info'
import ClusterHostGroupsPicklist from '../clusters/ClusterHostGroupsPicklist'
import DropdownField from 'core/components/validatedForm/DropdownField'
import { allVolumesSelector } from '../../storage/volumes/selectors'
import { listVolumes } from '../../storage/volumes/actions'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import { trackEvent } from 'utils/tracking'

const defaultParams = { valuesByHost: {} }

const ErrorText = ({ children }) => {
  const classes = useStyles({})
  return (
    <Text variant="body1" className={classes.errorText}>
      {children}
    </Text>
  )
}

const { nova, neutron } = ApiClient.getInstance()

export default function EditServerRolesModal({ rows: hosts, onClose, allHostsDetails }) {
  const classes = useStyles()
  const [submitting, setSubmitting] = useState(false)
  const [originalHostRoles, setOriginalHostRoles] = useState({ valuesByHost: {} })
  const [blueprintSettings, setBlueprintSettings] = useState({
    storageBackends: {},
    imageLibrarySharedStorage: false,
  })
  const [loaded, setLoaded] = useState(false)
  const [showRetryMessage, setShowRetryMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [vmsByHost, setVmsByHost] = useState({})
  const [healthCheckFailed, setHealthCheckFailed] = useState(null)

  useEffect(() => {
    const makeCalls = async () => {
      const hypervisorHosts = hosts.filter((host) => host.roles.includes('hypervisor'))
      const results: any = await Promise.allSettled(
        hypervisorHosts.map((host) => getVmsForHost(host?.id)),
      )
      const values = results?.map((result) => result?.value)
      const valuesByHost = hypervisorHosts.reduce(
        (accum, host, idx) => ({
          ...accum,
          [host.id]: values[idx],
        }),
        {},
      )
      setVmsByHost(valuesByHost)
    }
    makeCalls()
  }, [hosts])

  const volumeBackendConfigs = useMemo(() => {
    const volumeTypes = keys(blueprintSettings.storageBackends)
    const configurations = volumeTypes.reduce((accum, type) => {
      const configNames = keys(blueprintSettings.storageBackends[type])
      const configsWithVolumeType = configNames.map((name) => {
        return {
          // @ts-ignore
          ...blueprintSettings.storageBackends[type][name],
          name: name,
          volumeType: type,
        }
      })
      return [...accum, ...configsWithVolumeType]
    }, [])
    return configurations
  }, [blueprintSettings])

  const volumeBackendConfigOptions = useMemo(() => {
    return volumeBackendConfigs.map((config) => {
      return {
        label: `${config.name} (${config.volumeType}, ${config.driver})`,
        value: config.name,
      }
    })
  }, [volumeBackendConfigs])

  const allHosts = useSelector(resmgrHostsSelector)
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  useListAction(listVolumes)

  const allVolumes = useAppSelector(allVolumesSelector)

  const { reload } = useListAction(listResmgrHosts, {})

  const { loading: loadingImages } = useListAction(listImages)
  const imagesList = useAppSelector(imagesSelector)

  const enableRoleCheckbox = useCallback(
    (host, roleName, hasRoleFunc) => {
      if (params.valuesByHost?.[host.id]?.[roleName]) {
        return true
      }
      const roleHost = allHosts.find(hasRoleFunc)
      const roleChecked = hosts.some((h) => !!params.valuesByHost?.[h.id]?.[roleName])
      if (roleHost) {
        return roleHost.id === host.id
      }
      return !roleChecked
    },
    [allHosts, params, hosts],
  )

  const populateHostData = (hosts, storageHostBackends) => {
    return hosts.reduce(
      (accum, host) => ({
        valuesByHost: {
          ...accum.valuesByHost,
          [host.id]: {
            hostNetworkConfig: host?.hostconfig_id,
            hypervisor: hasHypervisorRole(host),
            imageLibrary: hasImageLibraryRole(host),
            remoteSupport: hasRemoteSupportRole(host),
            dns: hasDnsRole(host),
            volumeBackends:
              keys(storageHostBackends.find((h) => h.host.id === host.id)?.backends) || [],
            attachedVolumes: allVolumes?.filter((volume) =>
              volume['os-vol-host-attr:host']?.includes(host?.id),
            ),
            hostcluster: host?.cluster?.name,
          },
        },
      }),
      { valuesByHost: {} },
    )
  }

  useEffect(() => {
    if (loaded) {
      return
    }
    const querySettings = async () => {
      const blueprints = await getBlueprints()
      const blueprint = blueprints?.[0]
      // @ts-ignore
      setBlueprintSettings(blueprint)
      setLoaded(true)

      const storageHosts = hosts.filter((host) => host.roles.includes('persistent-storage'))
      const results = await Promise.allSettled(
        storageHosts.map((host) =>
          getResmgrRoleConfig({ role: 'pf9-cindervolume-config', host: host }),
        ),
      )
      const storageHostBackends = storageHosts.map((host, idx) => {
        return {
          host,
          // @ts-ignore
          backends: results[idx]?.value?.backends,
        }
      })
      const hostData = populateHostData(hosts, storageHostBackends)
      setOriginalHostRoles(hostData)
      setParams(hostData)
    }
    querySettings()
  }, [hosts])

  const checkServicesHealth = async () => {
    setHealthCheckFailed(null)
    try {
      await nova.checkHealth()
      await neutron.checkHealth()
      return true
    } catch (e) {
      setHealthCheckFailed({
        title: 'Error',
        message: 'Hypervisor services are not ready. Please try again after sometime',
      })
      return false
    }
  }

  // 🩹 Rollback imageLibrary checkbox state if failed
  const rollbackImageLibraryState = useCallback(
    (errHostId) => {
      const originalImageLibrary = originalHostRoles?.valuesByHost?.[errHostId]?.imageLibrary

      if (typeof originalImageLibrary !== 'boolean') {
        return
      }
      // Rollback the specific host's imageLibrary value
      setParams({
        ...params,
        valuesByHost: {
          ...(params?.valuesByHost ?? {}),
          [errHostId]: {
            ...(params?.valuesByHost?.[errHostId] ?? {}),
            imageLibrary: originalImageLibrary,
          },
        },
      })
    },
    [params, originalHostRoles, setParams],
  )

  const submitForm = useCallback(async () => {
    const rolesToAdd = []
    const rolesToRemove = []
    setSubmitting(true)

    // For enabling new hypervisor roles - Check health of nova and neutron services before submitting
    if (
      hosts.some((host) => {
        originalHostRoles?.valuesByHost?.[host.id]?.hypervisor === false &&
          params?.valuesByHost?.[host.id]?.hypervisor === true
      })
    ) {
      const heathCheckOk = await checkServicesHealth()
      if (!heathCheckOk) {
        setSubmitting(false)
        return
      }
    }

    for (const host of hosts) {
      if (
        (originalHostRoles?.valuesByHost?.[host.id]?.hypervisor === false &&
          params?.valuesByHost?.[host.id]?.hypervisor === true) ||
        originalHostRoles?.valuesByHost?.[host.id]?.hostcluster !==
          params?.valuesByHost?.[host.id]?.hostcluster
      ) {
        rolesToAdd.push({
          role: 'hypervisor',
          host,
          hostcluster: params?.valuesByHost?.[host.id]?.hostcluster,
        })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.imageLibrary === false &&
        params?.valuesByHost?.[host.id]?.imageLibrary === true
      ) {
        rolesToAdd.push({ role: 'imageLibrary', host })
      }
      if (
        params?.valuesByHost?.[host.id]?.volumeBackends?.length &&
        originalHostRoles?.valuesByHost?.[host.id]?.volumeBackends?.sort() !==
          params?.valuesByHost?.[host.id]?.volumeBackends?.sort()
      ) {
        rolesToAdd.push({
          role: 'persistentStorage',
          host,
          volumeBackends: params.valuesByHost[host.id].volumeBackends,
        })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.remoteSupport === false &&
        params?.valuesByHost?.[host.id]?.remoteSupport === true
      ) {
        rolesToAdd.push({ role: 'remoteSupport', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.dns === false &&
        params?.valuesByHost?.[host.id]?.dns === true
      ) {
        rolesToAdd.push({ role: 'dns', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.hypervisor === true &&
        params?.valuesByHost?.[host.id]?.hypervisor === false
      ) {
        rolesToRemove.push({ role: 'hypervisor', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.imageLibrary === true &&
        params?.valuesByHost?.[host.id]?.imageLibrary === false
      ) {
        rolesToRemove.push({ role: 'imageLibrary', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.volumeBackends.length &&
        !params?.valuesByHost?.[host.id]?.volumeBackends.length
      ) {
        rolesToRemove.push({ role: 'persistentStorage', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.remoteSupport === true &&
        params?.valuesByHost?.[host.id]?.remoteSupport === false
      ) {
        rolesToRemove.push({ role: 'remoteSupport', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.dns === true &&
        params?.valuesByHost?.[host.id]?.dns === false
      ) {
        rolesToRemove.push({ role: 'dns', host })
      }
    }

    // Need to unassign host configs that we are changing
    const addHostConfigHosts = hosts.filter((host) => {
      return host.hostconfig_id === null
    })

    const updateHostConfigHosts = hosts.filter((host) => {
      return (
        host.hostconfig_id &&
        host.hostconfig_id !== params?.valuesByHost?.[host.id]?.hostNetworkConfig
      )
    })

    const hostConfigUnassignmentResults = await Promise.allSettled(
      updateHostConfigHosts.map((host) => {
        return unassignHostConfig({
          hostId: host.id,
          configId: host.hostconfig_id,
        })
      }),
    )

    // Do not assign if they selected the None option
    const filteredConfigHosts = updateHostConfigHosts.filter(
      (host) => params?.valuesByHost?.[host.id]?.hostNetworkConfig !== 'none',
    )
    const hostConfigAssignmentResults = await Promise.allSettled(
      [...addHostConfigHosts, ...filteredConfigHosts].map((host) => {
        return assignHostConfig({
          hostId: host.id,
          configId: params?.valuesByHost?.[host.id]?.hostNetworkConfig,
        })
      }),
    )

    if (
      // @ts-ignore
      [...hostConfigAssignmentResults, ...hostConfigUnassignmentResults].some(
        (result) => result.status === 'rejected',
      )
    ) {
      // @ts-ignore
      const errIndex = hostConfigAssignmentResults.findIndex(
        (result) => result.status === 'rejected',
      )
      const errHost = hosts[errIndex]
      // @ts-ignore
      const err = `${errHost.info.hostname} - ${hostConfigAssignmentResults[errIndex]?.reason?.err}`
      setSubmitting(false)
      setErrorMessage(err)
      setShowRetryMessage(true)
      return
    }
    const results = await Promise.allSettled(
      rolesToAdd.map((role) => {
        if (role.role === 'hypervisor') {
          return assignHypervisorRole({ host: role.host, hostcluster: role.hostcluster })
        } else if (role.role === 'imageLibrary') {
          return assignImageLibraryRole({ host: role.host })
        } else if (role.role === 'persistentStorage') {
          return assignPersistentStorageRole({
            host: role.host,
            body: {
              backends: role.volumeBackends,
            },
          })
        } else if (role.role === 'remoteSupport') {
          return updateRemoteSupportRole({ host: role.host })
        } else if (role.role === 'dns') {
          return assignDns({ host: role.host })
        } else {
          return null
        }
      }),
    )
    trackEvent('Edit Roles - Add Roles', { roles: rolesToAdd })
    if (results.some((result) => result.status === 'rejected')) {
      trackEvent('Edit Roles - Add Roles Failed', { results })
      const errIndex = results.findIndex((result) => result.status === 'rejected')
      const errHost = rolesToAdd[errIndex]?.host
      // @ts-ignore
      const err = `${errHost.info.hostname} - ${results[errIndex]?.reason?.err}`

      if (errHost?.id) {
        rollbackImageLibraryState(errHost.id)
      }
      setSubmitting(false)
      setErrorMessage(err)
      setShowRetryMessage(true)
      return
    }
    // if (rolesToAdd.some((role) => role.role === 'hypervisor')) {
    //   await waitSeconds(5)
    // }

    const removeResults = await Promise.allSettled(
      rolesToRemove.map((role) => {
        if (role.role === 'imageLibrary') {
          return deauthorizeImageLibraryRole({ host: role.host })
        } else if (role.role === 'hypervisor') {
          return deauthorizeHypervisorRole({ host: role.host })
        } else if (role.role === 'persistentStorage') {
          return deauthorizePersistentStorageRole({ host: role.host })
        } else if (role.role === 'remoteSupport') {
          return removeRemoteSupportRole({ hostId: role.host.id })
        } else if (role.role === 'dns') {
          return removeDns({ hostId: role.host.id })
        }
        return null
      }),
    )
    trackEvent('Edit Roles - Remove Roles', { roles: rolesToRemove })
    if (removeResults.some((result) => result.status === 'rejected')) {
      trackEvent('Edit Roles - Remove Roles Failed', { results: removeResults })
      const errIndex = removeResults.findIndex((result) => result.status === 'rejected')
      const errHost = rolesToRemove[errIndex]?.host
      // @ts-ignore

      let errMsg = ''
      const removeResult = removeResults[errIndex]
      if (removeResult && removeResult.status === 'rejected') {
        // @ts-ignore
        errMsg = removeResult.reason?.err?.message || removeResult.reason?.err || 'Unknown error'
        // @ts-ignore
        if (removeResult.reason?.err?.code === 'ERR_BAD_RESPONSE') {
          errMsg = 'Something went wrong. Please try again.'
        }
      }

      const err = `${errHost.info.hostname} - ${errMsg}`
      if (errHost?.id) {
        rollbackImageLibraryState(errHost.id)
      }
      setSubmitting(false)
      setErrorMessage(err)
      setShowRetryMessage(true)
      return
    }
    reload(true, true)
    handleClose()
  }, [params, hosts])

  const handleClose = () => {
    setParams(defaultParams)
    onClose()
  }

  // Only one image library for now, so just check images list
  // const vmImageLength = (host) => {
  //   return host?.vms?.map((vm) => vm?.image).length
  // }

  const noImagesPresent = useMemo(() => {
    if (loadingImages) {
      return false
    }
    return imagesList.length === 0
  }, [imagesList, loadingImages])

  // Disable multiple image library hosts if shared storage is not enabled in blueprint
  // Only one host can have image library role at a time
  const isImageLibraryDisabled = useCallback(
    (hostId: string) => {
      const { imageLibrarySharedStorage } = blueprintSettings ?? {}
      const valuesByHost = (params?.valuesByHost ?? {}) as Record<
        string,
        { imageLibrary?: boolean }
      >

      // If shared storage is enabled, no restriction
      if (imageLibrarySharedStorage) {
        return false
      }

      // Check if any host already has the image-library role in the backend (saved state)
      const existingImageLibraryHost = allHostsDetails?.find((h) =>
        h.roles?.includes('image-library'),
      )?.id

      // Check if any host (in the form) currently has imageLibrary checked
      const selectedHostInForm = Object.entries(valuesByHost)?.find(
        ([, val]) => val?.imageLibrary,
      )?.[0]

      // If shared storage is disabled and another host (not this one) is selected in the form → disable
      if (selectedHostInForm && selectedHostInForm !== hostId) {
        return true
      }

      // If shared storage is disabled and there’s an existing image-library host,
      // only that host can be modified (unchecked). Others remain disabled.
      if (existingImageLibraryHost && existingImageLibraryHost !== hostId) {
        return true
      }

      // Allow toggling on/off for the same host (update allowed)
      return false
    },
    [allHostsDetails, blueprintSettings?.imageLibrarySharedStorage, params?.valuesByHost],
  )

  return (
    <ModalForm
      open
      title={`Edit Roles`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      submitTitle={`Update Role Assignment`}
      maxWidth={1200}
      loading={!loaded || submitting}
      loadingMessage={
        submitting ? 'Updating host roles, please do not close this dialog' : 'Loading'
      }
      panel="dialog"
      customErrorComponent={showRetryMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
      error={healthCheckFailed ? healthCheckFailed : null}
    >
      <div className={classes.planContainer}>
        <>
          <div className={classes.headerLabel}>
            <Text variant="caption1">Server</Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1" className={classes.hostConfig}>
              Host Config&nbsp;
              <HintTooltip
                message="For GPU hosts, select the gpu enabled host config"
                align={{ vertical: 'top', horizontal: 'middle' }}
              />
            </Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1">Hypervisor</Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1">Host Cluster</Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1">VM Image Library</Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1">Persistent Storage</Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1">Advanced Remote Support</Text>
          </div>
          <div className={classes.headerLabel}>
            <Text variant="caption1">DNS</Text>
          </div>
        </>
        {hosts.map((host) => {
          const isGPUSupportedHost = host?.gpuModels?.length > 0
          const configPicklistDisabled = host.v1Roles?.filter((role) => role !== 'pf9-support')
            ?.length
          return (
            <React.Fragment key={host.id}>
              <div className={classes.valueCell}>
                <Text variant="body2" className={classes.name}>
                  {host.info.hostname}
                </Text>
              </div>
              <div className={classes.valueCell}>
                <PicklistField
                  DropdownComponent={HostNetworkConfigPicklist}
                  name="hostNetworkConfig"
                  id={`valuesByHost.${host.id}.hostNetworkConfig`}
                  label=""
                  compact={false}
                  value={params.valuesByHost?.[host.id]?.hostNetworkConfig}
                  tooltip={
                    configPicklistDisabled ? (
                      <Text variant="body2" className={classes.tooltip}>
                        You may only change the network configuration when no roles are installed on
                        the host.
                      </Text>
                    ) : null
                  }
                  disabled={configPicklistDisabled}
                  disabledMessage
                  onChange={(value) =>
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          hostNetworkConfig: value,
                        },
                      },
                    })
                  }
                  required
                />
              </div>
              <div className={classes.valueCell}>
                <CheckboxField
                  id={`valuesByHost.${host.id}.hypervisor`}
                  label=""
                  value={params.valuesByHost?.[host.id]?.hypervisor}
                  // disabled={vmsByHost?.[host.id]?.length > 0}
                  info={
                    vmsByHost?.[host.id]?.length > 0
                      ? 'Cannot remove hypervisor role while VMs are present on this host'
                      : ''
                  }
                  onChange={(value) => {
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          hypervisor: value,
                        },
                      },
                    })
                  }}
                />
              </div>
              <div className={classes.valueCell}>
                <DropdownField
                  id={`${host.id}-hostcluster`}
                  DropdownComponent={ClusterHostGroupsPicklist}
                  value={params.valuesByHost?.[host.id]?.hostcluster}
                  onChange={(value) => {
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          hostcluster: value,
                        },
                      },
                    })
                  }}
                  disabled={
                    !params.valuesByHost?.[host.id]?.hypervisor ||
                    originalHostRoles?.valuesByHost?.[host.id]?.hostcluster
                  }
                  tooltip={
                    originalHostRoles?.valuesByHost?.[host.id]?.hostcluster
                      ? 'You can only change the cluster when the host is drained and deauthorized'
                      : !params.valuesByHost?.[host.id]?.hypervisor
                      ? 'You must assign a hypervisor role to the host'
                      : ''
                  }
                  filterFn={(cluster) => {
                    // Not non-gpu supported host - show only non-gpu enabled clusters
                    if (!isGPUSupportedHost) {
                      return !cluster?.gpu?.enabled
                    }

                    // GPU supported host - show only clusters with compatible gpu mode
                    if (cluster?.gpu?.enabled) {
                      return host?.compatibleModes?.includes(cluster?.gpu?.mode)
                    }

                    return false
                  }}
                  required={params.valuesByHost?.[host.id]?.hypervisor}
                />
              </div>
              <div className={classes.valueCell}>
                <CheckboxField
                  id={`valuesByHost.${host.id}.imageLibrary`}
                  label=""
                  value={params.valuesByHost?.[host.id]?.imageLibrary}
                  disabled={isImageLibraryDisabled(host.id)}
                  info={
                    isImageLibraryDisabled(host.id) ? (
                      'The image library role can be assigned to multiple hosts only if shared storage is enabled in the cluster blueprint. Remove the image library role from the other host and save the changes first.'
                    ) : !noImagesPresent && hasImageLibraryRole(host) ? (
                      <div>
                        <Text variant="body2" className={classes.tooltip}>
                          <b>WARNING</b>: If removing the image library role from this host, ensure
                          one of the following, or the images will become unusable after the role is
                          removed.
                        </Text>
                        <Text variant="body2" className={classes.tooltip}>
                          1. There will still be an existing image library host present, and the
                          hosts are using shared filesystem storage.
                        </Text>
                        <Text variant="body2" className={classes.tooltip}>
                          2. There will still be an existing image library host present, and the
                          image storage path on the blueprint was set to a volume type, meaning
                          images are stored on a persistent storage host.
                        </Text>
                        {!blueprintSettings?.imageLibrarySharedStorage && (
                          <Text variant="body2" className={classes.tooltip}>
                            3. The image library role must be removed from this host and saved
                            before it can be assigned to another host.
                          </Text>
                        )}
                      </div>
                    ) : (
                      ''
                    )
                  }
                  onChange={(value) =>
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          imageLibrary: value,
                        },
                      },
                    })
                  }
                />
              </div>
              <div className={classes.valueCell}>
                <MultiDropdownField
                  id={`valuesByHost.${host.id}.volumeBackends`}
                  label=""
                  options={volumeBackendConfigOptions}
                  value={params.valuesByHost?.[host.id]?.volumeBackends}
                  //disabled={params.valuesByHost?.[host.id]?.attachedVolumes?.length > 0}
                  tooltip={
                    params.valuesByHost?.[host.id]?.attachedVolumes?.length > 0 ? (
                      <Text variant="body2" className={classes.tooltip}>
                        <b>Warning</b>: You cannot remove this role because one or more volumes are
                        still attached. Please detach all volumes before proceeding.
                      </Text>
                    ) : (
                      ''
                    )
                  }
                  onChange={(value) =>
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          volumeBackends: value,
                        },
                      },
                    })
                  }
                  fullWidth
                />
                {/* <CheckboxField
                id={`valuesByHost.${host.id}.blockStorage`}
                label=""
                value={params.valuesByHost?.[host.id]?.blockStorage}
                info={
                  <Text variant="body2" className={classes.tooltip}>
                    <FontAwesomeIcon className={classes.tooltipIcon}>
                      exclamation-triangle
                    </FontAwesomeIcon>
                    Before making this host a block storage node, please ensure that the cinder
                    volume group has been created on this host.
                  </Text>
                }
                onChange={(value) =>
                  updateParams({
                    valuesByHost: {
                      ...params.valuesByHost,
                      [host.id]: {
                        ...params.valuesByHost[host.id],
                        blockStorage: value,
                      },
                    },
                  })
                }
              /> */}
              </div>
              <div className={classes.valueCell}>
                <CheckboxField
                  id={`valuesByHost.${host.id}.remoteSupport`}
                  label=""
                  value={params.valuesByHost?.[host.id]?.remoteSupport}
                  onChange={(value) =>
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          remoteSupport: value,
                        },
                      },
                    })
                  }
                />
              </div>
              <div className={classes.valueCell}>
                <CheckboxField
                  id={`valuesByHost.${host.id}.dnsRole`}
                  label=""
                  value={params.valuesByHost?.[host.id]?.dns}
                  disabled={!enableRoleCheckbox(host, 'dns', hasDnsRole)}
                  info={
                    !enableRoleCheckbox(host, 'dns', hasDnsRole)
                      ? 'Only one cluster server may be a dns role. Please deauthorize your existing dns if you would like to assign a new dns.'
                      : ''
                  }
                  onChange={(value) =>
                    updateParams({
                      valuesByHost: {
                        ...params.valuesByHost,
                        [host.id]: {
                          ...params.valuesByHost[host.id],
                          dns: value,
                        },
                      },
                    })
                  }
                />
              </div>
              {params.valuesByHost?.[host.id]?.dns && (
                <Info className={classes.instructions}>
                  <Text variant="body2">
                    To complete the process of making this host a Designate Node, you will need to
                    create a custom pools.yaml file.
                    <br />
                    Then log in to the host and run the command below with your custom pools.yaml
                    file:
                    <br />
                    <br />
                    <CodeBlock>
                      {'designate-manage pool update --file <path/to/pools.yaml>'}
                    </CodeBlock>
                    <br />
                    <br />
                    Refer to the following
                    <ExternalLink url={DNS_DOC_LINK} newWindow={true}>
                      {' '}
                      article{' '}
                    </ExternalLink>
                    for more information.
                  </Text>
                </Info>
              )}
            </React.Fragment>
          )
        })}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  formContainer: {
    padding: '24px',
    background: 'white',
    width: 'max-content',
  },
  hostValuesContainer: {
    display: 'grid',
    gap: '16.5px',
  },
  planContainer: {
    display: 'grid',
    gridTemplateColumns:
      'max-content max-content max-content max-content auto max-content max-content max-content',
    alignItems: 'center',
    gap: '12px 36px',
    overflowY: 'scroll',
    maxHeight: '360px',
    paddingBottom: 100,
  },
  headerLabel: {
    marginBottom: '25px',
  },
  hostConfig: {
    display: 'flex',
    alignItems: 'center',
  },
  valueCell: {
    // marginBottom: '25px',
    // display: 'flex',
    // justifyContent: 'center',
  },
  tooltip: {
    color: theme.components.tooltip.text,
  },
  tooltipIcon: {
    color: theme.components.tooltip.text,
    marginRight: 8,
  },
  errorText: {
    color: theme.components.graph.error,
  },
  name: {
    whiteSpace: 'nowrap',
  },
  instructions: {
    gridColumn: 'span 8',
  },
}))
